/**
 * OperationsDrilldown.jsx — Iter-360e (P0-356 follow-up)
 *
 * Single page that renders the drill-down list for any operations
 * tile whose count comes from `services/attendance_state.py`. The tile
 * count and this list share the exact same SQL predicate so they can
 * never disagree.
 *
 * Route: /admin/operations/drilldown/:key
 *   key ∈ {staff-payment-pending, clock-out-missing, ready-for-payment, recap-pending}
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Loader2, ExternalLink } from 'lucide-react';

const KEYS = {
  'staff-payment-pending': {
    title: 'Staff Payment Pending',
    subtitle: 'Bookings clocked out but not yet paid. Each row shows exactly why.',
    tone: 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-800',
  },
  'clock-out-missing': {
    title: 'Clock-Out Missing',
    subtitle: 'Talent clocked in but never clocked out. Past shift end + 30 min grace.',
    tone: 'bg-rose-50 border-rose-200 text-rose-800',
  },
  'ready-for-payment': {
    title: 'Ready for Payment',
    subtitle: 'Every requirement cleared. Safe to disburse now.',
    tone: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  },
  'recap-pending': {
    title: 'Recap Pending',
    subtitle: 'Shifts where the talent still owes a client rating.',
    tone: 'bg-amber-50 border-amber-200 text-amber-800',
  },
};

export default function OperationsDrilldown() {
  const { key } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const meta = KEYS[key] || { title: key, subtitle: '', tone: 'bg-slate-50 border-slate-200 text-slate-800' };

  useEffect(() => {
    (async () => {
      setLoading(true); setErr(null);
      try {
        const res = await api.get(`/admin/operations/${key}?limit=100`);
        setData(res.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Failed to load drill-down');
      } finally {
        setLoading(false);
      }
    })();
  }, [key]);

  if (!KEYS[key]) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Button variant="outline" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <p className="mt-4 text-slate-600">Unknown drill-down key: <code>{key}</code></p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`drilldown-${key}`}>
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-slate-900" data-testid="drilldown-title">{meta.title}</h1>
            <p className="text-xs text-slate-500 truncate">{meta.subtitle}</p>
          </div>
          {data && (
            <div className={`text-sm font-semibold px-3 py-1 rounded-full border ${meta.tone}`} data-testid="drilldown-total">
              {data.total} record{data.total === 1 ? '' : 's'}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {loading && (
          <div className="flex items-center justify-center py-12 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}
        {err && (
          <Card className="border-rose-200 bg-rose-50">
            <CardContent className="p-4 text-rose-700" data-testid="drilldown-error">{err}</CardContent>
          </Card>
        )}
        {!loading && !err && (data?.bookings?.length ?? 0) === 0 && (
          <Card>
            <CardContent className="p-8 text-center text-slate-500" data-testid="drilldown-empty">
              No records match. If the dashboard tile shows a non-zero count,
              this is a data-drift bug — please report it with the URL of this page.
            </CardContent>
          </Card>
        )}
        {!loading && !err && (data?.bookings?.length ?? 0) > 0 && (
          <div className="space-y-2">
            {data.bookings.map((b) => (
              <Card key={b.booking_id} className="hover:shadow-md transition-shadow"
                    data-testid={`drilldown-row-${b.booking_id}`}>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-slate-900">{b.talent_name || 'Unknown talent'}</span>
                        <Badge className="text-[10px] bg-slate-100 text-slate-700 border-slate-200">
                          {b.role_name || 'Role'}
                        </Badge>
                        {b.overall_status && (
                          <Badge className="text-[10px] bg-cyan-50 text-cyan-800 border-cyan-200">
                            {b.overall_status.replace(/_/g, ' ')}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-600">
                        {b.client_name || 'Client'} · {b.venue_name || 'Venue'}
                        {b.city && ` · ${b.city}${b.state ? `, ${b.state}` : ''}`}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        {b.shift_date} · {b.start_time?.slice(0, 5)}–{b.end_time?.slice(0, 5)}
                        {b.hours_worked ? ` · ${b.hours_worked}h worked` : ''}
                        {' · '}payment: <span className="font-semibold">{b.payment_status || 'pending'}</span>
                      </p>
                      {/* Blockers = why payment is still pending. */}
                      {b.blockers?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {b.blockers.map((blk) => (
                            <Badge
                              key={blk.code}
                              className={`text-[10px] ${
                                blk.informational
                                  ? 'bg-slate-100 text-slate-700 border-slate-200'
                                  : 'bg-rose-50 text-rose-700 border-rose-300'
                              }`}
                              title={blk.message}
                              data-testid={`blocker-${blk.code}`}
                            >
                              {blk.label}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5 sm:items-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/shifts/${b.shift_id}`)}
                        data-testid={`open-shift-${b.booking_id}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open shift
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/booking/${b.booking_id}/activity`)}
                        data-testid={`open-activity-${b.booking_id}`}
                        className="text-xs text-cyan-700"
                      >
                        View activity
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {data.total > data.bookings.length && (
              <p className="text-xs text-slate-500 text-center py-2">
                Showing {data.bookings.length} of {data.total}. Pagination coming next iteration.
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
